import type { PassioCallRecord } from "@passio/api-client";
import type { ChainCallRecord } from "@passio/easyai-client";
import { easyAiOrigin, publicApiBase } from "./clients";
import { pathAndQuery } from "./codegen";

const PLACEHOLDERS: Record<string, string> = { authorization: "Bearer <access_token>", "passio-id": "<customer_id>" };

/**
 * One call as a single JSON document: request and response with every header, as it would look
 * against the API directly. Credentials are placeholders; `X-Demo-*` headers come from this demo's
 * proxy, not the API, so they're left out.
 */
export function passioExchange(r: PassioCallRecord) {
  const requestHeaders = Object.fromEntries(Object.entries(r.requestHeaders).map(([k, v]) => [k, PLACEHOLDERS[k.toLowerCase()] ?? v]));
  const responseHeaders = Object.fromEntries(Object.entries(r.meta?.raw ?? {}).filter(([k]) => !k.startsWith("x-demo-")));
  return {
    request: {
      method: r.method,
      url: publicApiBase() + pathAndQuery(r),
      headers: requestHeaders,
      ...(r.requestBody !== undefined ? { body: r.requestBody } : {}),
    },
    response: {
      status: r.status,
      durationMs: r.durationMs,
      headers: responseHeaders,
      body: r.data ?? null,
      ...(r.error ? { error: r.error } : {}),
    },
  };
}

/** A chain run as its two calls: execute, then the last poll of the execution. */
export function chainExchange(r: ChainCallRecord) {
  const passioApi = r.api === "passio";
  const base = passioApi ? publicApiBase() : easyAiOrigin().replace(/\/+$/, "");
  const auth: { Authorization: string; "Passio-ID"?: string } = passioApi ? { Authorization: PLACEHOLDERS.authorization, "Passio-ID": PLACEHOLDERS["passio-id"] } : { Authorization: "Bearer <EASYAI_API_KEY>" };
  const executePath = r.executePath ?? `/api/v2/chains/${r.chainId}/execute`;
  const pollPath = (id: string) => (r.executionPath ?? "/api/v2/chains/executions/{executionId}").replace("{executionId}", id);
  return {
    execute: {
      request: {
        method: "POST",
        url: `${base}${executePath}`,
        headers: { ...auth, Accept: "application/json", "Content-Type": "application/json" },
        body: r.requestBody,
      },
      response: r.executionId ? { body: passioApi ? { id: r.executionId } : { id: r.executionId, chain_id: r.chainId } } : { error: r.error ?? null, status: r.httpStatus ?? null },
    },
    poll: r.executionId
      ? {
          request: { method: "GET", url: `${base}${pollPath(r.executionId)}`, headers: { ...auth, Accept: "application/json" } },
          polls: r.polls,
          response: { body: r.execution ?? null },
        }
      : null,
    durationMs: r.durationMs,
    status: r.status,
  };
}
